import React, { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  Plus,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  ArrowRightLeft,
  Package,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";
import { HelpButton } from "@/components/help-button";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";

// Mock data types
export interface StockTransfer {
  id: string;
  reference: string;
  status: "draft" | "pending" | "completed" | "cancelled";
  createdAt: string;
  createdBy: string;
  completedAt?: string;
  itemCount: number;
  totalQuantity: number;
  notes?: string;
}

// Mock data
const mockTransfers: StockTransfer[] = [
  {
    id: "TRF-001",
    reference: "TRF-2024-001",
    status: "completed",
    createdAt: "2024-01-15T10:30:00",
    createdBy: "John Smith",
    completedAt: "2024-01-15T11:45:00",
    itemCount: 3,
    totalQuantity: 150,
    notes: "Monthly stock rebalancing",
  },
  {
    id: "TRF-002",
    reference: "TRF-2024-002",
    status: "draft",
    createdAt: "2024-01-16T09:15:00",
    createdBy: "Sarah Johnson",
    itemCount: 2,
    totalQuantity: 75,
  },
  {
    id: "TRF-003",
    reference: "TRF-2024-003",
    status: "pending",
    createdAt: "2024-01-16T14:20:00",
    createdBy: "Mike Wilson",
    itemCount: 5,
    totalQuantity: 200,
    notes: "Urgent transfer for customer order",
  },
];

const getStatusColor = (status: StockTransfer["status"]) => {
  switch (status) {
    case "draft":
      return "bg-gray-500/10 text-gray-500 border-gray-500/20";
    case "pending":
      return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
    case "completed":
      return "bg-green-500/10 text-green-500 border-green-500/20";
    case "cancelled":
      return "bg-red-500/10 text-red-500 border-red-500/20";
    default:
      return "bg-gray-500/10 text-gray-500 border-gray-500/20";
  }
};

const getStatusIcon = (status: StockTransfer["status"]) => {
  switch (status) {
    case "draft":
      return <Edit className="h-3 w-3" />;
    case "pending":
      return <Clock className="h-3 w-3" />;
    case "completed":
      return <CheckCircle2 className="h-3 w-3" />;
    case "cancelled":
      return <XCircle className="h-3 w-3" />;
    default:
      return null;
  }
};

export function StockTransfersPage() {
  const { navigateTo } = useNavigation();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | "draft" | "pending" | "completed">("all");

  // Filter transfers based on search and tab
  const filteredTransfers = useMemo(() => {
    let filtered = mockTransfers;

    // Filter by tab
    if (activeTab !== "all") {
      filtered = filtered.filter((transfer) => transfer.status === activeTab);
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (transfer) =>
          transfer.reference.toLowerCase().includes(query) ||
          transfer.createdBy.toLowerCase().includes(query) ||
          transfer.notes?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [searchQuery, activeTab]);

  // Calculate metrics
  const metrics = useMemo(() => {
    const drafts = mockTransfers.filter((t) => t.status === "draft").length;
    const pending = mockTransfers.filter((t) => t.status === "pending").length;
    const completed = mockTransfers.filter((t) => t.status === "completed").length;
    const totalItems = mockTransfers.reduce((sum, t) => sum + t.itemCount, 0);

    return { drafts, pending, completed, totalItems };
  }, []);

  const handleCreateTransfer = () => {
    navigateTo("stock-transfers/create/step1");
  };

  const handleViewTransfer = (transferId: string) => {
    toast.info(`Viewing transfer ${transferId}`);
    // TODO: Navigate to view/edit page
  };

  const handleEditTransfer = (transferId: string) => {
    toast.info(`Editing transfer ${transferId}`);
    // TODO: Navigate to edit page
  };

  const handleDeleteTransfer = (transferId: string) => {
    toast.success(`Transfer ${transferId} deleted`);
    // TODO: Implement delete functionality
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Stock Transfers</h1>
          <p className="text-muted-foreground mt-1">
            Transfer stock between locations and manage transfer records
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton contentKey="stock-transfers" />
          <Button onClick={handleCreateTransfer} size="default">
            <Plus className="h-4 w-4 mr-2" />
            Create Transfer
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <StaticMetricCardAlt
          title="Draft Transfers"
          value={metrics.drafts.toString()}
          icon={Edit}
          trend="neutral"
        />
        <StaticMetricCardAlt
          title="Pending Transfers"
          value={metrics.pending.toString()}
          icon={Clock}
          trend="neutral"
        />
        <StaticMetricCardAlt
          title="Completed Today"
          value={metrics.completed.toString()}
          icon={CheckCircle2}
          trend="up"
          trendValue="+12%"
        />
        <StaticMetricCardAlt
          title="Total Items"
          value={metrics.totalItems.toString()}
          icon={Package}
          trend="neutral"
        />
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Transfer Records</CardTitle>
              <CardDescription>View and manage all stock transfers</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Tabs and Search */}
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-auto">
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="draft">Draft</TabsTrigger>
                  <TabsTrigger value="pending">Pending</TabsTrigger>
                  <TabsTrigger value="completed">Completed</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="relative w-64">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search transfers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            {/* Table */}
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Total Qty</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransfers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        {searchQuery
                          ? "No transfers found matching your search"
                          : activeTab === "all"
                          ? "No transfers yet. Create your first transfer to get started."
                          : `No ${activeTab} transfers`}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTransfers.map((transfer) => (
                      <TableRow key={transfer.id}>
                        <TableCell className="font-medium">{transfer.reference}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getStatusColor(transfer.status)}>
                            <span className="flex items-center gap-1">
                              {getStatusIcon(transfer.status)}
                              {transfer.status.charAt(0).toUpperCase() + transfer.status.slice(1)}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell>{transfer.createdBy}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(transfer.createdAt)}
                        </TableCell>
                        <TableCell>{transfer.itemCount}</TableCell>
                        <TableCell>{transfer.totalQuantity}</TableCell>
                        <TableCell className="max-w-[200px] truncate text-muted-foreground">
                          {transfer.notes || "-"}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleViewTransfer(transfer.id)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              {transfer.status === "draft" && (
                                <DropdownMenuItem onClick={() => handleEditTransfer(transfer.id)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Transfer
                                </DropdownMenuItem>
                              )}
                              {transfer.status === "draft" && (
                                <DropdownMenuItem
                                  onClick={() => handleDeleteTransfer(transfer.id)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
